import { useCallback, useMemo, useState, type ReactNode } from 'react'
import {
  AlertIcon,
  CheckIcon,
  CrossIcon,
  DataSurface,
  Pagination,
  usePagination,
} from '../../../shared/components'
/* ⚠️ From `ui/icons` directly — `shared/components` re-exports the two this file
   already used and not this one. `DashStatus` imports it the same way. */
import { InfoIcon } from '../../../shared/ui/icons'
import { useResource } from '../../../shared/hooks/useResource'
import {
  getAttendance,
  saveAttendance,
  type AttendanceView,
} from '../../../shared/api/attendance'
import type { StaffAuth } from '../../../shared/api/teaching'
import { TEACHER_COPY, describeError, type Lang } from './copy'
import { AttendanceMonthPanel } from './AttendanceMonthPanel'
import {
  absentFrom,
  applyMark,
  hasChanges,
  unreachableAbsentCount,
} from './attendanceRegister'

/**
 * T7 — the class register.
 *
 * 🔴 THE CAPABILITY EXISTED AND THE SHAPE DID NOT. `POST /players/:id/absent`
 * has been built, scoped and idempotent since §8.5, and the app called it from
 * exactly one place: a single child's detail page. A teacher taking a register
 * for twenty-one children had to open twenty-one screens. Attendance is a
 * class-at-a-time act and every route for it was child-at-a-time.
 *
 * 🔴 THE DRAFT IS LOCAL AND SAVING IS DELIBERATE, BECAUSE THE SAVE MESSAGES
 * FAMILIES.
 *
 * Every tap here changes local state only. The server is told once, and it
 * enqueues a WhatsApp message for each child who was **not already** absent —
 * so a teacher who taps the wrong row and corrects it before saving has told
 * nobody. A live per-tap toggle would have sent that message, and a WhatsApp
 * message cannot be recalled. `sendWarning` says so above the button, not in a
 * confirmation after it.
 *
 * 🔴 THE MARK IS "PRESENT OR ABSENT", NOT A ROW YOU CLICK. See `PresenceChoice`
 * for the whole argument. The short version: this screen used to draw one green
 * pill per child reading "Here", with the `<tr>` as an undisclosed toggle — so
 * the register read as a report, "absent" was reachable only by discovering the
 * row was clickable, and a stray click on a parent's name marked a child absent
 * and messaged that parent. Two named, idempotent, keyboard-reachable buttons.
 *
 * ⚠️ **THE LAYOUT IS INVENTED.** `wireframes.md`'s D-section has no register.
 * OD-55 is the precedent for inventing one and saying so. 📋 Reported.
 */

export interface AttendanceScreenProps {
  classId: string
  lang?: Lang
  auth?: StaffAuth
  /** The roster changed underneath the class-level screens. */
  onSaved?: () => void
  /**
   * 🔴 MAY THIS PERSON *TAKE* THE REGISTER, OR ONLY READ IT?
   *
   * E4 let a subject teacher open a class they are not the teacher of record
   * for. The server has always refused them this one write —
   * `AttendanceService.writableClass` asks `class.teacherId === principal.userId`
   * directly rather than going through `ScopeService`, because taking a register
   * MESSAGES FAMILIES and a parent must hear about their child from the one
   * teacher who is answerable for them.
   *
   * ⚠️ IT DECIDES WHAT IS DRAWN, NEVER WHAT IS ALLOWED — D4. The refusal is the
   * server's; this is what stops it arriving as a 403 after a teacher has
   * marked twenty children.
   *
   * ⚠️ DEFAULTS TO `true`, so the screen's own behaviour is unchanged for every
   * caller that has no opinion. `mayTakeRegister` is where the branch lives.
   */
  canTakeRegister?: boolean
  /** Who does take it, for the sentence that says so. Null when nobody does. */
  registerHeldBy?: string | null
}

export function AttendanceScreen({
  classId,
  lang = 'en',
  auth,
  onSaved,
  canTakeRegister = true,
  registerHeldBy = null,
}: AttendanceScreenProps) {
  const t = TEACHER_COPY[lang]

  const resource = useResource<AttendanceView>(
    (signal) => getAttendance(classId, { ...auth, signal }),
    ['attendance', classId],
    { isEmpty: (v) => v.rows.length === 0 },
  )

  const [draft, setDraft] = useState<ReadonlySet<string> | null>(null)

  const serverAbsent = useMemo(
    () => absentFrom(resource.data?.rows ?? []),
    [resource.data],
  )
  const absent = draft ?? serverAbsent

  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState<{ absent: number; notified: number } | null>(null)
  const [failed, setFailed] = useState(false)

  /**
   * 🔴 SET THE STATE, DO NOT FLIP IT.
   *
   * The row used to be one `<tr onClick>` toggle, and a toggle is the wrong
   * verb for a register. A teacher reading down a class does not think "change
   * Amaira" — she thinks "Amaira is present", and if she is unsure whether her
   * last tap landed, a toggle punishes the second tap by undoing the first.
   * "Present" and "Absent" are two idempotent buttons: pressing the one that is
   * already on is a no-op, so tapping twice is safe on the screen that messages
   * a parent.
   */
  const setPresence = useCallback(
    (playerId: string, isAbsent: boolean) => {
      setSaved(null)
      setDraft((current) => {
        const next = applyMark(current ?? serverAbsent, playerId, isAbsent)
        /* ⚠️ `applyMark` hands back the same set when nothing changed, so a
           no-op tap leaves an untouched draft untouched — and Save unlit. */
        return next === (current ?? serverAbsent) ? current : next
      })
    },
    [serverAbsent],
  )

  const markAllPresent = useCallback(() => {
    setSaved(null)
    setDraft(new Set())
  }, [])

  const save = useCallback(async () => {
    setSaving(true)
    setFailed(false)
    try {
      const result = await saveAttendance(classId, [...absent], auth)
      setSaved({ absent: result.newlyAbsent, notified: result.notified })
      setDraft(null)
      resource.retry()
      onSaved?.()
    } catch {
      setFailed(true)
    } finally {
      setSaving(false)
    }
  }, [classId, absent, auth, resource, onSaved])

  const rows = resource.data?.rows ?? []

  /**
   * 🔴 DIRTY IS A COMPARISON, NOT "HAS THE DRAFT BEEN TOUCHED".
   *
   * It was `draft !== null`, so marking a child absent and putting them back
   * left Save lit on a register identical to the stored one — and pressing it
   * posted a save whose only honest outcome was "0 marked absent". Worse, "Mark
   * all present" on a class where everyone was already present lit it too. Save
   * now offers itself only when there is something to save.
   */
  const dirty = useMemo(() => hasChanges(draft, serverAbsent), [draft, serverAbsent])

  /**
   * 🔴 THE CHILDREN THIS SAVE WOULD RECORD BUT NOT ANNOUNCE.
   *
   * `parentReachable` has been on every row since the endpoint was built and no
   * screen had ever drawn it. A teacher marking a child absent reasonably
   * assumes the parent is told; for a child with no linked parent, or one who
   * has not opted into WhatsApp, nothing is sent. Counted over what is marked
   * right now, not over the class, so it answers "will this save reach
   * everyone" rather than reciting a roster problem on a day it does not bite.
   */
  const unreachableAbsent = useMemo(
    () => unreachableAbsentCount(rows, absent),
    [rows, absent],
  )

  /*
   * ⚠️ THERE IS DELIBERATELY NO "the register has not been taken yet" BANNER.
   * `Absence` stores absences only, so a day everyone was present stores
   * nothing and is indistinguishable from a day nobody took the register —
   * `copy.ts`'s month block names the same limit. Drawing "not taken yet" over
   * an empty day would state a fact the product does not have.
   */

  return (
    <section className="font-dash text-dash-sm text-ink max-w-5xl mx-auto pb-12">
      {/* ── Page Header Row ── */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-dash text-2xl font-extrabold text-ink">{t.attendanceTitle}</h1>
          <p className="mt-1 font-dash text-dash-xs font-bold text-muted">
            {resource.data ? t.attendanceFor(resource.data.day) : t.attendanceHint}
          </p>
          {/* 🔴 The instruction survives the day loading — it was the fallback for it.
              ⚠️ AND IT IS NOT SHOWN TO SOMEBODY WHO CANNOT MARK ANYTHING: *"mark
              each child present or absent"* is an instruction they cannot follow. */}
          {resource.data !== null && canTakeRegister && (
            <p className="mt-0.5 font-dash text-dash-xs font-medium text-muted">
              {t.attendanceHint}
            </p>
          )}
        </div>
        {/*
          🔴 NOT DISABLED — ABSENT. D4: a greyed "Save the register" is still a
          promise that a teacher will keep hunting for the state that unlocks it,
          and there is none. The sentence below the heading says who takes it.
        */}
        <div className={canTakeRegister ? 'flex items-center gap-3' : 'hidden'}>
          {/*
            ⚠️ DISABLED WHEN EVERY CHILD IS ALREADY MARKED PRESENT, so it stops
            being a button that visibly does nothing — and stops lighting Save
            on a register it did not change.
          */}
          <button
            type="button"
            disabled={absent.size === 0}
            onClick={markAllPresent}
            className="rounded-full border border-line bg-surface px-4 py-2 font-dash text-dash-xs font-bold text-ink transition-colors hover:bg-canvas shadow-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            {absent.size === 0 ? t.markAllPresentDone : t.markAllPresent}
          </button>
          <button
            type="button"
            disabled={saving || !dirty}
            onClick={() => void save()}
            className="rounded-full bg-primary text-white px-5 py-2 font-dash text-dash-xs font-bold shadow-sm transition-colors hover:bg-primary-deep disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            {saving ? t.savingRegister : t.saveRegister}
          </button>
        </div>
      </div>

      {/*
        🔴 WHO TAKES THIS REGISTER, SAID BEFORE THE TABLE RATHER THAN AFTER A
        FAILED SAVE. It sits outside the `DataSurface` on purpose: it is a fact
        about this person and this class, true while the day is still loading and
        true if it fails to load at all.
      */}
      {!canTakeRegister && (
        <div className="mb-6 rounded-2xl border border-line bg-canvas p-3.5">
          <p className="flex items-start gap-2.5 font-dash text-dash-xs font-bold text-ink">
            <InfoIcon size={16} className="mt-px shrink-0" />
            <span>{t.registerReadOnly(registerHeldBy)}</span>
          </p>
          {/* ⚠️ The reason, not just the rule. A teacher told only "you cannot"
              reads it as a bug in the product or a slight from the office. */}
          <p className="mt-1.5 pl-[26px] font-dash text-dash-xs font-medium text-muted">
            {t.registerReadOnlyWhy}
          </p>
        </div>
      )}

      <DataSurface
        state={resource.state}
        skeletonRows={8}
        emptyMessage={t.emptyClass}
        errorMessage={describeError(resource.error, lang)}
        errorLastWorked={
          resource.lastLoadedAt
            ? t.lastWorked(resource.lastLoadedAt.toLocaleTimeString())
            : t.neverWorked
        }
        errorRetryLabel={t.retry}
        onRetry={resource.retry}
      >
        {resource.data === null ? null : (
          <>
            {/* ── Status Banners ── */}
            {(saved !== null || failed) && (
              <div className="mb-6 space-y-3">
                {saved !== null && (
                  <div
                    role="status"
                    className="rounded-2xl border border-pill-success-line bg-pill-success-bg p-3.5 text-dash-xs text-pill-success-fg font-bold flex items-center gap-2.5"
                  >
                    <CheckIcon size={16} className="shrink-0" />
                    <span>{t.savedRegister(saved.absent, saved.notified)}</span>
                  </div>
                )}
                {failed && (
                  <div
                    role="alert"
                    className="rounded-2xl border border-pill-danger-line bg-pill-danger-bg p-3.5 text-dash-xs text-pill-danger-fg font-bold flex items-center gap-2.5"
                  >
                    <AlertIcon size={16} className="shrink-0" />
                    <span>{t.saveRegisterFailed}</span>
                  </div>
                )}
              </div>
            )}

            {/*
              🔴 BOTH NUMBERS, AND `aria-live` SO THE COUNT IS AUDIBLE.
              Marking a row is otherwise a silent change to a screen reader —
              the pressed state moves, but the tally nobody can see does not
              announce itself.
            */}
            <p
              aria-live="polite"
              className="mb-2 font-dash text-dash-xs font-medium text-muted"
            >
              {t.absentCount(absent.size, rows.length)}
            </p>

            {/* ── Attendance Register Table Card ── */}
            <PaginatedAttendanceTable
              rows={rows}
              absent={absent}
              setPresence={setPresence}
              canTakeRegister={canTakeRegister}
              t={t}
            />

            {/* ── Bottom Save Register Footer Bar ──
                ⚠️ THE WHOLE BAR GOES, NOT JUST THE BUTTON IN IT. What is left
                otherwise is a warning about messages that will not be sent and a
                count already printed above the table. */}
            <div
              className={
                canTakeRegister
                  ? 'mt-4 rounded-2xl border border-line bg-surface p-4 shadow-card'
                  : 'hidden'
              }
            >
              {/*
                🔴 THE WARNING THE FILE'S OWN DOCBLOCK PROMISED AND THE SCREEN
                NEVER DREW. `sendWarning` has sat in `copy.ts` unused since T7
                shipped, while the comment above claimed *"the warning says so
                before the button, not in a confirmation after it."* It does
                now, and only when there is actually something to send — a
                standing threat above an all-present register is noise a teacher
                learns to read past, and then it is not there on the day it
                matters.
              */}
              {absent.size > 0 && (
                <div className="mb-3.5 space-y-2">
                  <p className="flex items-start gap-2.5 font-dash text-dash-xs font-bold text-pill-warning-fg">
                    <AlertIcon size={16} className="mt-px shrink-0" />
                    <span>{t.sendWarning}</span>
                  </p>
                  {unreachableAbsent > 0 && (
                    <p className="flex items-start gap-2.5 font-dash text-dash-xs font-medium text-muted">
                      <AlertIcon size={16} className="mt-px shrink-0" />
                      <span>{t.unreachableNote(unreachableAbsent)}</span>
                    </p>
                  )}
                </div>
              )}
              <div className="flex items-center justify-between gap-4">
                <span className="font-dash text-dash-xs font-medium text-muted">
                  {t.absentCount(absent.size, rows.length)}
                </span>
                <button
                  type="button"
                  disabled={saving || !dirty}
                  onClick={() => void save()}
                  className="rounded-full bg-primary text-white px-5 py-2 font-dash text-dash-xs font-bold shadow-sm transition-colors hover:bg-primary-deep disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  {saving ? t.savingRegister : t.saveRegister}
                </button>
              </div>
            </div>
          </>
        )}
      </DataSurface>

      {/*
        🔴 THE MONTH SITS UNDER TODAY, NOT BESIDE IT, AND THE ORDER IS THE
        POINT. This screen's job at 09:05 is marking one register; the month is
        what a teacher opens at a parents' evening or when she notices a child
        keeps missing Mondays. Putting a read-only grid alongside the editable
        one would also invite the mistake the whole feature has to avoid —
        tapping a past day expecting it to change.

        ⚠️ IT IS OUTSIDE THE `DataSurface` ABOVE, so a class whose TODAY failed
        to load still shows its history rather than one error swallowing both.
        Its own four states are its own — §8.4.
      */}
      <div className="mt-8">
        <AttendanceMonthPanel classId={classId} lang={lang} auth={auth} />
      </div>
    </section>
  )
}

function PaginatedAttendanceTable({
  rows,
  absent,
  setPresence,
  canTakeRegister,
  t,
}: {
  rows: AttendanceView['rows']
  /** ⚠️ Readonly — the table reads the draft, it never edits it in place. */
  absent: ReadonlySet<string>
  setPresence: (playerId: string, isAbsent: boolean) => void
  /** False for somebody who may read this register but not take it. */
  canTakeRegister: boolean
  t: (typeof TEACHER_COPY)[Lang]
}) {
  const { paginatedItems, page, totalPages, setPage, totalItems } = usePagination(rows, 7)

  return (
    <>
      <div className="rounded-2xl border border-line bg-surface overflow-hidden shadow-card">
        <div className="scroll-x" tabIndex={0}>
        <table className="w-full border-collapse text-left font-dash text-dash-xs">
          <caption className="sr-only">{t.attendanceTitle}</caption>
          <thead>
            <tr className="bg-[#fffdf5] border-b border-line text-ink font-extrabold">
              <th scope="col" className="px-5 py-3.5">{t.roll}</th>
              <th scope="col" className="px-5 py-3.5">{t.student}</th>
              {/* ⚠️ "Today", not "Status", when the cell is a fact rather than a
                  control — the column heading has to describe what is under it. */}
              <th scope="col" className="px-5 py-3.5">
                {canTakeRegister ? t.attendanceStatus : t.attendanceToday}
              </th>
              <th scope="col" className="px-5 py-3.5">{t.parent}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {paginatedItems.map((row) => {
              const isAbsent = absent.has(row.playerId)
              return (
                <tr key={row.playerId} className="hover:bg-canvas transition-colors">
                  <td className="px-5 py-4 font-extrabold text-ink">
                    {row.rollNumber ?? '—'}
                  </td>
                  <th scope="row" className="px-5 py-4 text-left font-extrabold text-ink">
                    {row.name}
                  </th>
                  <td className="px-5 py-4">
                    {/*
                      🔴 A STATUS, NOT TWO DISABLED BUTTONS. A greyed pair of
                      Present/Absent controls on every row reads as a screen that
                      is broken or still loading, twenty times over; one pill
                      states the mark the class teacher made and nothing more.
                    */}
                    {canTakeRegister ? (
                      <PresenceChoice
                        name={row.name}
                        isAbsent={isAbsent}
                        onChoose={(next) => setPresence(row.playerId, next)}
                        t={t}
                      />
                    ) : (
                      <span
                        className={`inline-flex items-center gap-1.5 rounded-pill border px-3.5 py-1.5 font-dash text-dash-xs font-bold ${
                          isAbsent
                            ? 'bg-pill-danger-bg text-pill-danger-fg border-pill-danger-line'
                            : 'bg-pill-success-bg text-pill-success-fg border-pill-success-line'
                        }`}
                      >
                        <span aria-hidden="true" className="inline-flex shrink-0">
                          {isAbsent ? <CrossIcon size={14} /> : <CheckIcon size={14} />}
                        </span>
                        {isAbsent ? t.absent : t.present}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4 font-bold text-muted">
                    {row.parentName ? (
                      row.parentName
                    ) : (
                      <span className="font-medium">—</span>
                    )}
                    {/*
                      🔴 SAID ON THE ROW, AND ONLY ON THE ROWS IT IS TRUE OF.
                      A named parent can still be unreachable — deactivated, no
                      phone, or not opted into WhatsApp — so this is keyed on
                      `parentReachable`, never on whether the name column is
                      blank. Marking this child records the absence and tells
                      nobody, and the fix is a roster linkage the school owns.
                    */}
                    {!row.parentReachable && (
                      <span className="mt-1 flex items-center gap-1.5 font-dash text-dash-xs font-medium text-pill-warning-fg">
                        <AlertIcon size={13} className="shrink-0" />
                        {t.noParentLinked}
                      </span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        </div>
      </div>
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        onPageChange={setPage}
        totalItems={totalItems}
      />
    </>
  )
}

/**
 * One child's mark — two buttons, not one toggle.
 *
 * 🔴 THE ROW WAS AN INVISIBLE TOGGLE AND THAT WAS THE BUG THE SCREENSHOT SHOWS.
 * Every child rendered as one green pill reading "Here", the whole `<tr>` was
 * the click target, and nothing on screen said the pill was pressable or what
 * pressing it would do. A register drawn that way has three failures at once:
 * it looks like a report rather than a form, "absent" is a state you can only
 * reach by discovering the row is clickable, and a stray click anywhere on the
 * row — on the parent's name, on the roll number — silently marks a child
 * absent on the one screen that messages that child's parent.
 *
 * 🔴 BOTH ANSWERS ARE ALWAYS ON SCREEN. The teacher's mark is a choice between
 * two named options, so both are drawn, both are reachable by Tab, and the one
 * in force is carried by `aria-pressed` + fill + icon + word — never by colour
 * alone (design-system.md §12).
 *
 * 🔴 AND IT IS IDEMPOTENT. Pressing "Present" on a child already present does
 * nothing at all, which is what makes a second, unsure tap safe. The toggle it
 * replaced turned that same tap into a silent mark-absent.
 */
function PresenceChoice({
  name,
  isAbsent,
  onChoose,
  t,
}: {
  name: string
  isAbsent: boolean
  onChoose: (isAbsent: boolean) => void
  t: (typeof TEACHER_COPY)[Lang]
}) {
  return (
    <div
      role="group"
      aria-label={t.presenceFor(name)}
      className="inline-flex items-center gap-1 rounded-pill border border-line bg-canvas p-1"
    >
      <PresenceButton
        pressed={!isAbsent}
        label={t.present}
        accessibleLabel={t.markPresent(name)}
        icon={<CheckIcon size={14} />}
        onClick={() => onChoose(false)}
        pressedClassName="bg-pill-success-bg text-pill-success-fg border-pill-success-line"
      />
      <PresenceButton
        pressed={isAbsent}
        label={t.absent}
        accessibleLabel={t.markAbsent(name)}
        icon={<CrossIcon size={14} />}
        onClick={() => onChoose(true)}
        pressedClassName="bg-pill-danger-bg text-pill-danger-fg border-pill-danger-line"
      />
    </div>
  )
}

function PresenceButton({
  pressed,
  label,
  accessibleLabel,
  icon,
  onClick,
  pressedClassName,
}: {
  pressed: boolean
  label: string
  accessibleLabel: string
  icon: ReactNode
  onClick: () => void
  pressedClassName: string
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      aria-label={accessibleLabel}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-pill border px-3.5 py-1.5 font-dash text-dash-xs font-bold transition-colors cursor-pointer ${
        pressed
          ? `${pressedClassName} shadow-sm`
          : 'border-transparent text-muted hover:bg-surface'
      }`}
    >
      {/*
        ⚠️ THE ICON IS HIDDEN AND THE WORD IS NOT. Both marks carry an icon so
        the pressed one is legible without colour, but a screen reader already
        has `aria-label` and `aria-pressed` — announcing the glyph too would
        read the row twice.
      */}
      <span aria-hidden="true" className="inline-flex shrink-0">
        {icon}
      </span>
      {label}
    </button>
  )
}
